if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButtons = Array.from(
          this.querySelectorAll('button[type="submit"], input[type="submit"]')
        );

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.error = false;
        this.isSubmitting = false;
        this.activeSubmitButton = null;
        this.activeSpinner = null;

        if (!this.form || !this.variantIdInput) return;

        /*
         * Shopify may render the variant input disabled when the initially
         * selected variant is sold out. Variant-selection JavaScript will
         * manage the state afterward.
         */
        if (this.variantIdInput.value) {
          this.variantIdInput.disabled = false;
        }

        this.form.addEventListener(
          'submit',
          this.onSubmitHandler.bind(this)
        );

        if (this.cart) {
          this.submitButtons.forEach((button) => {
            button.setAttribute('aria-haspopup', 'dialog');
          });
        }
      }

      async onSubmitHandler(event) {
        event.preventDefault();

        if (this.isSubmitting) return;

        const submitter =
          event.submitter ||
          this.querySelector('[type="submit"]:not([disabled])');

        if (!submitter) return;

        if (
          submitter.disabled ||
          submitter.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        const variantId = this.parsePositiveInteger(
          this.variantIdInput.value
        );

        if (!variantId) {
          this.handleErrorMessage('Please select an available option.');
          return;
        }

        const customizer = this.getProductCustomizer();

        if (customizer && !customizer.validate()) {
          return;
        }

        this.handleErrorMessage();
        this.error = false;
        this.isSubmitting = true;
        this.setLoadingState(submitter, true);

        const formData = new FormData(this.form);
        const quantity = Math.max(
          1,
          this.parsePositiveInteger(formData.get('quantity')) || 1
        );

        const customizerItems =
          customizer?.getAddOnItems({
            quantity,
          }) || [];

        const hasConfiguredItems = customizerItems.length > 0;
        const bundleId = hasConfiguredItems
          ? this.createBundleId()
          : '';

        /*
         * Complimentary/upsell products now use their own direct quick-add
         * button and are intentionally not bundled into the main Add to Cart.
         */
        const configuredItems =
          customizer?.getAddOnItems({
            bundleId,
            quantity,
          }) || [];

        /* Main merchandise stays variant-only. */
        const mainItem = {
          id: variantId,
          quantity,
        };

        const sellingPlan = this.parsePositiveInteger(
          formData.get('selling_plan')
        );

        if (sellingPlan) {
          mainItem.selling_plan = sellingPlan;
        }

        const items = [mainItem, ...configuredItems];
        const checkoutRequested =
          submitter.dataset.checkout === 'true' ||
          submitter.classList.contains('pers_btn');

        try {
          const response = await this.addItems(items);

          if (response.status) {
            throw this.createCartError(response);
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: String(variantId),
            cartData: response,
          });

          this.error = false;

          if (checkoutRequested) {
            window.location.assign(
              window.routes?.checkout_url || '/checkout'
            );
            return;
          }

          if (!this.cart) {
            window.location.assign(window.routes.cart_url);
            return;
          }

          const quickAddModal = this.closest('quick-add-modal');

          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                window.setTimeout(() => {
                  this.cart.renderContents(response);
                });
              },
              { once: true }
            );

            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }

          this.dispatchEvent(
            new CustomEvent('product-form:added', {
              bubbles: true,
              detail: {
                items,
                response,
                bundleId,
              },
            })
          );
        } catch (error) {
          console.error(error);

          const message =
            error?.description ||
            error?.message ||
            'Something went wrong while adding the products. Please try again.';

          this.error = true;
          this.handleErrorMessage(message);

          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: String(variantId),
            errors: error?.errors || message,
            message,
          });

          this.showSoldOutStateIfNeeded(error);
        } finally {
          this.isSubmitting = false;
          this.setLoadingState(submitter, false);

          if (
            this.cart &&
            this.cart.classList.contains('is-empty')
          ) {
            this.cart.classList.remove('is-empty');
          }
        }
      }

      async addItems(items) {
        const payload = { items };

        if (this.cart?.getSectionsToRender) {
          payload.sections = this.cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',');

          payload.sections_url = window.location.pathname;
          this.cart.setActiveElement?.(
            this.activeSubmitButton || document.activeElement
          );
        }

        const response = await fetch(
          window.routes?.cart_add_url || '/cart/add.js',
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(payload),
          }
        );

        let data;

        try {
          data = await response.json();
        } catch (error) {
          throw new Error(
            'The cart returned an invalid response. Please refresh and try again.'
          );
        }

        if (!response.ok || data.status) {
          throw this.createCartError(data);
        }

        return data;
      }

      getSelectedUpsellItems({ bundleId = '', quantity = 1 } = {}) {
        const productInfo =
          this.closest('product-info') ||
          this.closest('.shopify-section') ||
          document;

        const normalizedQuantity = Math.max(
          1,
          this.parsePositiveInteger(quantity) || 1
        );

        return Array.from(
          productInfo.querySelectorAll(
            '[data-product-upsells] [data-upsell-item].is-selected'
          )
        )
          .map((item) => {
            const variantId = this.parsePositiveInteger(
              item.dataset.variantId
            );

            if (!variantId) return null;

            return {
              id: variantId,
              quantity: normalizedQuantity,
              properties: {
                ...(bundleId ? { '_Bundle ID': bundleId } : {}),
                '_Bundle role': 'Upsell',
              },
            };
          })
          .filter(Boolean);
      }

      getProductCustomizer() {
        const productInfo =
          this.closest('product-info') ||
          this.closest('.shopify-section');

        const root =
          productInfo?.querySelector('[data-product-customizer]') ||
          document.querySelector(
            `[data-product-customizer][data-main-form-id="${CSS.escape(
              this.form.id
            )}"]`
          );

        return root?.productCustomizer || null;
      }

      getFormProperties(formData) {
        const properties = {};

        for (const [name, rawValue] of formData.entries()) {
          const match = name.match(/^properties\[(.+)]$/);
          if (!match) continue;

          const key = match[1].trim();
          const value =
            typeof rawValue === 'string'
              ? rawValue.trim()
              : rawValue;

          if (!key || value === '' || value == null) continue;

          properties[key] = value;
        }

        return properties;
      }

      createBundleId() {
        if (window.crypto?.randomUUID) {
          return `bundle-${window.crypto.randomUUID()}`;
        }

        return `bundle-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 10)}`;
      }

      createCartError(response = {}) {
        const error = new Error(
          response.description ||
            response.message ||
            'Unable to add the selected products.'
        );

        error.status = response.status;
        error.description = response.description;
        error.errors = response.errors;

        return error;
      }

      showSoldOutStateIfNeeded(error) {
        const message = String(
          error?.description || error?.message || ''
        ).toLowerCase();

        if (
          !message.includes('sold out') &&
          !message.includes('not enough') &&
          !message.includes('unavailable')
        ) {
          return;
        }

        const soldOutMessage =
          this.activeSubmitButton?.querySelector(
            '.sold-out-message'
          );

        if (!soldOutMessage) return;

        this.activeSubmitButton.setAttribute(
          'aria-disabled',
          'true'
        );

        const normalText =
          this.activeSubmitButton.querySelector(
            '[data-submit-text]'
          ) ||
          this.activeSubmitButton.querySelector('span');

        normalText?.classList.add('hidden');
        soldOutMessage.classList.remove('hidden');
      }

      setLoadingState(button, loading) {
        this.activeSubmitButton = loading ? button : null;

        if (loading) {
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('loading');

          this.activeSpinner =
            button.querySelector('.loading__spinner') ||
            this.querySelector('.loading__spinner');

          this.activeSpinner?.classList.remove('hidden');

          this.submitButtons.forEach((submitButton) => {
            if (submitButton !== button) {
              submitButton.setAttribute(
                'aria-disabled',
                'true'
              );
            }
          });

          return;
        }

        button.classList.remove('loading');
        this.activeSpinner?.classList.add('hidden');
        this.activeSpinner = null;

        if (!this.error) {
          button.removeAttribute('aria-disabled');

          this.submitButtons.forEach((submitButton) => {
            if (!submitButton.disabled) {
              submitButton.removeAttribute('aria-disabled');
            }
          });
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const primaryButton =
          this.querySelector(
            '[id^="ProductSubmitButton-"]'
          ) || this.submitButtons[0];

        if (!primaryButton) return;

        const buttonText =
          primaryButton.querySelector('[data-submit-text]') ||
          primaryButton.querySelector('span');

        if (disable) {
          primaryButton.setAttribute('disabled', 'disabled');

          if (text && buttonText) {
            buttonText.textContent = text;
          }
        } else {
          primaryButton.removeAttribute('disabled');

          if (buttonText) {
            buttonText.textContent =
              window.variantStrings?.addToCart ||
              'Add to cart';
          }
        }
      }

      parsePositiveInteger(value) {
        const number = Number.parseInt(value, 10);
        return Number.isFinite(number) && number > 0
          ? number
          : null;
      }

      get variantIdInput() {
        return this.form?.querySelector('[name="id"]');
      }
    }
  );
}